export const SCALE_FACTOR = 5; // 1 foot = 5 pixels

export const parseShapeJson = (shapeJson) => {
  if (!shapeJson) return null;
  if (typeof shapeJson === "object") return shapeJson;
  try {
    return JSON.parse(shapeJson);
  } catch (error) {
    return null;
  }
};

export const normalizeBoothShape = (shapeJson, dimensions = { length: 10, breadth: 10 }) => {
  const parsedShape = parseShapeJson(shapeJson);
  const rawParts = Array.isArray(parsedShape?.parts) ? parsedShape.parts : [];

  const parts = rawParts
    .map((part, index) => ({
      id: part.id || `part_${index + 1}`,
      x: Math.max(0, Number(part.x) || 0),
      y: Math.max(0, Number(part.y) || 0),
      width: Math.max(1, Number(part.width) || 1),
      height: Math.max(1, Number(part.height) || 1),
    }))
    .filter((part) => part.width > 0 && part.height > 0);

  if (parts.length === 0) {
    return {
      type: "rectangle",
      parts: [
        {
          id: "part_1",
          x: 0,
          y: 0,
          width: Math.max(1, Number(dimensions.length) || 10),
          height: Math.max(1, Number(dimensions.breadth) || 10),
        },
      ],
    };
  }

  return {
    type: parts.length > 1 ? "multi_rect" : parsedShape?.type || "rectangle",
    parts,
  };
};

export const getShapeBounds = (shapeJson, dimensions) => {
  const shape = normalizeBoothShape(shapeJson, dimensions);
  const maxX = Math.max(...shape.parts.map((part) => part.x + part.width));
  const maxY = Math.max(...shape.parts.map((part) => part.y + part.height));

  return {
    width: Math.max(1, maxX),
    height: Math.max(1, maxY),
  };
};

export const buildDefaultBoothShape = (dimensions = { length: 10, breadth: 10 }) => ({
  type: "rectangle",
  parts: [
    {
      id: "part_1",
      x: 0,
      y: 0,
      width: Math.max(1, Number(dimensions.length) || 10),
      height: Math.max(1, Number(dimensions.breadth) || 10),
    },
  ],
});

export const getAdaptiveFontSize = ({
  width,
  height,
  text = "",
  base = 14,
  min = 4,
  max = 16,
  padding = 4,
  lineHeight = 1.15,
} = {}) => {
  const usableWidth = Math.max(1, Number(width) - padding * 2);
  const usableHeight = Math.max(1, Number(height) - padding * 2);
  const lines = String(text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const lineCount = Math.max(1, lines.length);
  const longestLineLength = Math.max(1, ...lines.map((line) => line.length));
  const widthLimitedSize = usableWidth / (longestLineLength * 0.58);
  const heightLimitedSize = usableHeight / (lineCount * lineHeight);
  const fittedSize = Math.min(base, max, widthLimitedSize, heightLimitedSize);

  return Math.max(min, Math.floor(fittedSize * 10) / 10);
};

// Default initial nodes and edges for new floors
export const getDefaultNodes = () => {
  const rectWidth = 169 * SCALE_FACTOR; // 169ft = 845px (horizontal)
  const rectHeight = 100 * SCALE_FACTOR; // 100ft = 500px (vertical)
  
  // Position rectangle so its center is at origin (0, 0)
  const centerX = -rectWidth / 2;
  const centerY = -rectHeight / 2;

  const rectangle = {
    id: "rectangle-1",
    type: "border",
    position: { x: centerX, y: centerY },
    width: rectWidth,
    height: rectHeight,
    data: {
      label: undefined,
      width: rectWidth,
      height: rectHeight,
      borderWidth: 0.5,
      borderColor: "#000000",
      borderStyle: "solid",
      backgroundColor: "transparent",
      cornerRadius: 0,
      locked: true,
    },
    draggable: false,
    selectable: false,
  };

  const doorSize = 80;
  const doorSpacing = 0.2;

  const topLeftDoor = {
    id: "double-door-top-left",
    type: "gate",
    position: {
      x: centerX - doorSize + 25,
      y: centerY + rectHeight * doorSpacing - doorSize / 2,
    },
    width: doorSize,
    height: doorSize,
    data: {
      label: "",
      elementType: "double-door",
      iconPath: "double-door.svg",
      width: doorSize,
      height: doorSize,
      backgroundColor: "transparent",
      rotation: 270,
      locked: true,
    },
    draggable: false,
    selectable: true,
  };

  const bottomLeftDoor = {
    id: "double-door-bottom-left",
    type: "gate",
    position: {
      x: centerX - doorSize + 25,
      y: centerY + rectHeight * (1 - doorSpacing) - doorSize / 2,
    },
    width: doorSize,
    height: doorSize,
    data: {
      label: "",
      elementType: "double-door",
      iconPath: "double-door.svg",
      width: doorSize,
      height: doorSize,
      backgroundColor: "transparent",
      rotation: 270,
      locked: true,
    },
    draggable: false,
    selectable: true,
  };

  const topRightDoor = {
    id: "double-door-top-right",
    type: "gate",
    position: {
      x: centerX + rectWidth - 25,
      y: centerY + rectHeight * doorSpacing - doorSize / 2,
    },
    width: doorSize,
    height: doorSize,
    data: {
      label: "",
      elementType: "double-door",
      iconPath: "double-door.svg",
      width: doorSize,
      height: doorSize,
      backgroundColor: "transparent",
      rotation: 90,
      locked: true,
    },
    draggable: false,
    selectable: true,
  };

  const bottomRightDoor = {
    id: "double-door-bottom-right",
    type: "gate",
    position: {
      x: centerX + rectWidth - 25,
      y: centerY + rectHeight * (1 - doorSpacing) - doorSize / 2,
    },
    width: doorSize,
    height: doorSize,
    data: {
      label: "",
      elementType: "double-door",
      iconPath: "double-door.svg",
      width: doorSize,
      height: doorSize,
      backgroundColor: "transparent",
      rotation: 90,
      locked: true,
    },
    draggable: false,
    selectable: true,
  };

  return [
    rectangle,
    topLeftDoor,
    bottomLeftDoor,
    topRightDoor,
    bottomRightDoor,
  ];
};

export const getDefaultEdges = () => [];
